import { t } from '@/lib/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Id } from '@shared/types';
import {
  api,
  ApiError,
  type ActivityFilter,
  type ArtworkFilter,
  type ArtworkUploadFields,
  type AssetFilter,
  type PresetInput,
} from './api';

function retryUnlessAuth(count: number, err: unknown) {
  if (err instanceof ApiError && (err.isAuthError || err.status === 404)) return false;
  return count < 2;
}

export const keys = {
  me: ['me'] as const,
  folders: ['folders'] as const,
  assets: (f: AssetFilter) => ['assets', f] as const,
  asset: (id: Id) => ['asset', id] as const,
  search: (p: unknown) => ['search', p] as const,
  facets: ['facets'] as const,
  prompt: ['tag-prompt'] as const,
  queue: ['tag-queue'] as const,
  stats: ['library-stats'] as const,
  favIds: ['favourite-ids'] as const,
  users: ['users'] as const,
  downloadFormats: (id: Id) => ['download-formats', id] as const,
  presets: ['download-presets'] as const,
  basket: ['basket'] as const,
  recycleBin: ['recyclebin'] as const,
  shares: ['shares'] as const,
  activity: (f: ActivityFilter) => ['activity', f] as const,
  activityStats: (days: number) => ['activity-stats', days] as const,
  artwork: (f: ArtworkFilter) => ['artwork', f] as const,
  artworkGroup: (k: string) => ['artwork-group', k] as const,
  artworkBrands: ['artwork-brands'] as const,
  artworkStatus: ['artwork-status'] as const,
};

/**
 * Type-ahead for the rail's search box.
 *
 * `q` is expected to be already debounced by the caller — the endpoint is rate
 * limited to 120/min per IP and each call fans out across several indexed
 * queries, so firing on every keystroke would burn the budget and then start
 * failing. Two characters is the server's own floor; below that it returns
 * nothing, so there is no point asking.
 *
 * Results are kept while the next query loads (`placeholderData`) so the list
 * doesn't blank out between keystrokes, and cached briefly since the same few
 * prefixes get retyped constantly.
 */
export function useSuggest(q: string) {
  const term = q.trim();
  return useQuery({
    queryKey: ['suggest', term] as const,
    queryFn: () => api.suggest(term).then((r) => r.items),
    enabled: term.length >= 2,
    retry: false,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

/**
 * The artwork index changes only when someone adds artwork, and those
 * mutations invalidate these queries, so they are cached aggressively rather
 * than revalidated.
 */
const ARTWORK_STALE = 30 * 60_000;

export function useArtwork(f: ArtworkFilter, enabled = true) {
  return useQuery({
    queryKey: keys.artwork(f),
    queryFn: () => api.artwork(f).then((r) => r.items),
    enabled,
    retry: retryUnlessAuth,
    staleTime: ARTWORK_STALE,
  });
}

export function useArtworkGroup(pdfKey: string | undefined) {
  return useQuery({
    queryKey: keys.artworkGroup(pdfKey ?? ''),
    queryFn: () => api.artworkGroup(pdfKey as string),
    enabled: Boolean(pdfKey),
    retry: retryUnlessAuth,
    staleTime: ARTWORK_STALE,
  });
}

export function useArtworkBrands() {
  return useQuery({
    queryKey: keys.artworkBrands,
    queryFn: () => api.artworkBrands().then((r) => r.items),
    retry: retryUnlessAuth,
    staleTime: ARTWORK_STALE,
  });
}

export function useArtworkStatus() {
  return useQuery({
    queryKey: keys.artworkStatus,
    queryFn: () => api.artworkStatus(),
    retry: retryUnlessAuth,
    staleTime: ARTWORK_STALE,
  });
}

export function useMe() {
  return useQuery({ queryKey: keys.me, queryFn: () => api.me().then((r) => r.user), retry: retryUnlessAuth, staleTime: 15 * 60_000 });
}
export function useFolders() {
  return useQuery({ queryKey: keys.folders, queryFn: () => api.folders(), retry: retryUnlessAuth, staleTime: 60_000 });
}
export function useAssets(f: AssetFilter) {
  return useQuery({ queryKey: keys.assets(f), queryFn: () => api.assets(f), retry: retryUnlessAuth, staleTime: 15_000 });
}
/**
 * Direct file children of one folder, for the sidebar tree's leaf rows. Only
 * fetched once a node is expanded (`enabled`) so opening the app doesn't pull
 * every folder's contents. `recursive` is omitted on purpose — subfolders are
 * their own nodes, so a node shows only what sits directly inside it.
 */
/** File rows shown per folder in the rail before paging. */
export const RAIL_FILES_PER_PAGE = 50;

/**
 * Files directly inside one rail folder, paged.
 *
 * perPage was 500, which put a scroll-swallowing wall of rows under a folder
 * like PRODUCT_IMAGES (1,400 files) the moment it was opened. Paging keeps the
 * tree navigable; `search` filters server-side so a name can be found without
 * stepping through 28 pages.
 */
export function useFolderFiles(
  folderId: Id | undefined,
  enabled: boolean,
  opts: { page?: number; search?: string } = {},
) {
  const filter: AssetFilter = {
    folderId,
    sort: 'name',
    perPage: RAIL_FILES_PER_PAGE,
    page: opts.page ?? 1,
    // Omitted rather than sent empty, so an untouched filter box doesn't
    // change the query key and refetch.
    ...(opts.search?.trim() ? { search: opts.search.trim() } : {}),
  };
  return useQuery({
    queryKey: keys.assets(filter),
    queryFn: () => api.assets(filter),
    enabled: enabled && folderId !== undefined,
    retry: retryUnlessAuth,
    staleTime: 15_000,
    // Keep the previous page visible while the next loads, so the tree doesn't
    // collapse to a spinner on every page step.
    placeholderData: (prev) => prev,
  });
}
export function useAsset(id: Id | undefined) {
  return useQuery({ queryKey: keys.asset(id ?? 0), queryFn: () => api.asset(id as Id).then((r) => r.asset), enabled: id !== undefined, retry: retryUnlessAuth });
}
export function useSearch(params: Record<string, unknown>, enabled = true) {
  // AI searches are slow and the tags change rarely: keep results for an hour and
  // don't rerun them for ten minutes, so going back to a search is instant.
  return useQuery({
    queryKey: keys.search(params), queryFn: () => api.search(params), enabled, retry: retryUnlessAuth,
    staleTime: 10 * 60_000, gcTime: 60 * 60_000,
  });
}
/**
 * Filter options for the search form.
 *
 * `params` is the active query: passing it makes the image/file type counts
 * track what is actually selected instead of reporting library-wide totals.
 * Omit it where the options are just a vocabulary (the sidebar facet links).
 */
export function useFacets(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: [...keys.facets, params] as const,
    queryFn: () => api.facets(params),
    retry: retryUnlessAuth,
    staleTime: 60_000,
    gcTime: 60 * 60_000,
    // Keep the old counts on screen while the new ones load, so the pills don't
    // flash empty on every keystroke.
    placeholderData: (prev) => prev,
  });
}

/** Adding artwork changes the index, so every artwork query is refetched. */
function invalidateArtwork(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['artwork'] });
  void qc.invalidateQueries({ queryKey: keys.artworkStatus });
  void qc.invalidateQueries({ queryKey: keys.artworkBrands });
}

export function useArtworkUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { fields: ArtworkUploadFields; pdf: File; replace: boolean }) =>
      api.artworkUpload(v.fields, v.pdf, v.replace),
    onSuccess: (res) => {
      if (res.success) invalidateArtwork(qc);
    },
  });
}

/** Exposed so the bulk drop can refresh the index once, after its last batch. */
export function useInvalidateArtwork() {
  const qc = useQueryClient();
  return () => invalidateArtwork(qc);
}

export function useArtworkIngest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.artworkIngest(),
    onSuccess: (res) => {
      if (res.added.length) invalidateArtwork(qc);
    },
  });
}

export function useUpload(folderId: Id | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.upload(folderId, file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['assets'] });
      void qc.invalidateQueries({ queryKey: keys.folders });
    },
  });
}

/**
 * Move an asset to another folder.
 *
 * Invalidates both the asset lists and the folder tree: the asset leaves one
 * folder and joins another, so per-folder counts on both sides are stale.
 */
/**
 * Move several assets at once.
 *
 * Sequential rather than parallel: each move is a Pimcore save that writes a
 * version and touches the tree, and firing dozens concurrently invites the
 * deadlocks Pimcore's own save loop retries against. Failures are collected
 * rather than thrown, so one bad item does not strand the rest.
 */
export function useMoveAssets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, folderId }: { ids: Id[]; folderId: Id | null }) => {
      const failed: { id: Id; error: string }[] = [];
      for (const id of ids) {
        try {
          await api.moveAsset(id, folderId);
        } catch (e) {
          failed.push({ id, error: e instanceof Error ? e.message : t('queries.failed') });
        }
      }
      return { moved: ids.length - failed.length, failed };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['assets'] });
      void qc.invalidateQueries({ queryKey: keys.folders });
      void qc.invalidateQueries({ queryKey: keys.stats });
    },
  });
}

export function useMoveAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, folderId }: { id: Id; folderId: Id | null }) => api.moveAsset(id, folderId),
    onSuccess: (_r, { id }) => {
      void qc.invalidateQueries({ queryKey: ['assets'] });
      void qc.invalidateQueries({ queryKey: keys.asset(id) });
      void qc.invalidateQueries({ queryKey: keys.folders });
      void qc.invalidateQueries({ queryKey: keys.stats });
    },
  });
}

export function useCreateFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, parentId }: { name: string; parentId: Id | null }) => api.createFolder(name, parentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.folders }),
  });
}

export function useUpdateMetadata(id: Id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (metadata: Record<string, unknown>) => api.updateMetadata(id, metadata),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.asset(id) });
      void qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export function useTagNow(id: Id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.tagNow(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.asset(id) }),
  });
}
export function useConfirmTags(id: Id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (confirmed: boolean) => api.confirmTags(id, confirmed),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.asset(id) });
      void qc.invalidateQueries({ queryKey: ['search'] });
    },
  });
}

/**
 * Backend capabilities. Drives read-only affordances in the UI so controls that
 * cannot work are disabled with a reason rather than failing on click.
 */
export function useHealth() {
  return useQuery({ queryKey: ['health'], queryFn: () => api.health(), staleTime: 60_000, retry: false });
}

/** Whole-library counts, or one folder's (recursive) when `folderId` is given. */
export function useLibraryStats(folderId?: Id) {
  return useQuery({
    queryKey: folderId ? [...keys.stats, folderId] : keys.stats,
    queryFn: () => api.libraryStats(folderId),
    retry: retryUnlessAuth,
    staleTime: 30_000,
  });
}
export function useFavouriteIds() {
  return useQuery({ queryKey: keys.favIds, queryFn: () => api.favouriteIds().then((r) => new Set(r.ids)), retry: retryUnlessAuth, staleTime: 30_000 });
}
export function useToggleFavourite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: Id) => api.toggleFavourite(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.favIds });
      void qc.invalidateQueries({ queryKey: keys.stats });
      void qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export function useUsers() {
  return useQuery({ queryKey: keys.users, queryFn: () => api.users().then((r) => r.items), retry: retryUnlessAuth });
}
export function useSetUserActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: Id; isActive: boolean }) => api.setUserActive(id, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.users }),
  });
}
export function useSetUserAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isAdmin }: { id: Id; isAdmin: boolean }) => api.setUserAdmin(id, isAdmin),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.users }),
  });
}

export function useCreateFolderAt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, parentId }: { name: string; parentId: Id | null }) => api.createFolder(name, parentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.folders }),
  });
}
export function useRenameFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: Id; name: string }) => api.renameFolder(id, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.folders }),
  });
}
export function useDeleteFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: Id) => api.deleteFolder(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.folders });
      void qc.invalidateQueries({ queryKey: ['assets'] });
      void qc.invalidateQueries({ queryKey: keys.recycleBin });
    },
  });
}

/**
 * Delete one or more assets.
 *
 * Loops client-side like `useMoveAssets` rather than adding a bulk route: the
 * per-asset permission check and the per-asset recycle-bin snapshot both happen
 * server-side per id anyway, and a partial failure is normal (a contributor may
 * hold `delete` on some folders but not others). Collecting the handles is what
 * makes an undo of the whole batch possible.
 */
export function useDeleteAssets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: Id[]) => {
      const handles: number[] = [];
      const failed: { id: Id; error: string }[] = [];
      for (const id of ids) {
        try {
          const r = await api.deleteAsset(id);
          if (r.recycleBinId != null) handles.push(r.recycleBinId);
        } catch (e) {
          failed.push({ id, error: e instanceof Error ? e.message : t('queries.failed') });
        }
      }
      return { deleted: ids.length - failed.length, failed, handles };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['assets'] });
      void qc.invalidateQueries({ queryKey: keys.folders });
      void qc.invalidateQueries({ queryKey: keys.stats });
      void qc.invalidateQueries({ queryKey: keys.recycleBin });
    },
  });
}

/**
 * Put a deleted asset or folder back, using the recycle-bin handle the delete
 * returned. Invalidates the same keys a delete does, since the tree and any
 * listing both change again.
 */
export function useRestore() {
  const qc = useQueryClient();
  return useMutation({
    // Accepts one handle or many so a bulk delete can be undone as one action.
    // Restores in reverse order: a folder delete bins the parent last, so
    // putting the parent back first gives the children somewhere to land.
    mutationFn: async (recycleBinId: number | number[]) => {
      const ids = Array.isArray(recycleBinId) ? [...recycleBinId].reverse() : [recycleBinId];
      let last: { id: number | null; path: string } | undefined;
      for (const id of ids) last = await api.restoreAsset(id);
      return last;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.folders });
      void qc.invalidateQueries({ queryKey: ['assets'] });
      void qc.invalidateQueries({ queryKey: keys.recycleBin });
    },
  });
}

/** The recycle bin page's list. Always refetched on open: deletes happen in other tabs too. */
export function useRecycleBin() {
  return useQuery({ queryKey: keys.recycleBin, queryFn: api.recycleBin, staleTime: 0, refetchOnMount: 'always' });
}

/* ---------- downloads ---------- */

/** Which sized-download options an asset supports (built-in formats + types). */
export function useDownloadFormats(id: Id | undefined) {
  return useQuery({
    queryKey: keys.downloadFormats(id ?? 0),
    queryFn: () => api.downloadFormats(id as Id),
    enabled: id !== undefined,
    retry: retryUnlessAuth,
    // Depends only on the asset's mime type, so it won't change under us.
    staleTime: 10 * 60_000,
  });
}

/** The caller's saved presets plus any shared ones. */
export function useDownloadPresets() {
  return useQuery({
    queryKey: keys.presets,
    queryFn: () => api.downloadPresets().then((r) => r.presets),
    retry: retryUnlessAuth,
    staleTime: 60_000,
  });
}

export function useSavePreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: PresetInput & { id?: Id }) =>
      id === undefined ? api.createPreset(body) : api.updatePreset(id, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.presets }),
  });
}

export function useDeletePreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: Id) => api.deletePreset(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.presets }),
  });
}

/* ---------- share basket ---------- */

export function useBasket() {
  return useQuery({
    queryKey: keys.basket,
    queryFn: () => api.basket().then((r) => r.items),
    retry: retryUnlessAuth,
    staleTime: 30_000,
  });
}

export function useAddToBasket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { assetId: Id; size?: number; quality?: number | null; dpi?: number | null }) =>
      api.addToBasket(body),
    // The response already carries the new basket, so seed it rather than
    // triggering a second round-trip.
    onSuccess: (r) => qc.setQueryData(keys.basket, r.items),
  });
}

export function useRemoveFromBasket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assetId: Id) => api.removeFromBasket(assetId),
    onSuccess: (r) => qc.setQueryData(keys.basket, r.items),
  });
}

export function useClearBasket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.clearBasket(),
    onSuccess: () => qc.setQueryData(keys.basket, []),
  });
}

export function useShares() {
  return useQuery({
    queryKey: keys.shares,
    queryFn: () => api.shares().then((r) => r.shares),
    retry: retryUnlessAuth,
    staleTime: 30_000,
  });
}

export function useCreateShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { expiryDays: number; title?: string | null }) => api.createShare(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.shares });
      void qc.invalidateQueries({ queryKey: keys.basket });
    },
  });
}

export function useRevokeShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: Id) => api.revokeShare(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.shares }),
  });
}

/** Recipient side — public, so no auth retry rules apply. */
export function useSharedView(token: string | undefined) {
  return useQuery({
    queryKey: ['shared', token],
    queryFn: () => api.sharedView(token as string),
    enabled: Boolean(token),
    retry: false,
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: () => api.logout(), onSuccess: () => qc.clear() });
}

/* ---------- activity log (admin) ---------- */

export function useActivity(f: ActivityFilter) {
  return useQuery({
    queryKey: keys.activity(f),
    queryFn: () => api.activity(f),
    retry: retryUnlessAuth,
    // The feed is append-only and read constantly while filtering; a short
    // window keeps paging snappy without showing a stale head for long.
    staleTime: 10_000,
  });
}

export function useActivityStats(days: number) {
  return useQuery({
    queryKey: keys.activityStats(days),
    queryFn: () => api.activityStats(days),
    retry: retryUnlessAuth,
    staleTime: 30_000,
  });
}

export function usePruneActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (days: number) => api.pruneActivity(days),
    // Pruning changes both the feed and every rollup, so drop all of it.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['activity'] });
      void qc.invalidateQueries({ queryKey: ['activity-stats'] });
    },
  });
}
